// @ts-nocheck
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { 
  ArrowLeft, 
  Save, 
  Play, 
  Eye, 
  Plus,
  Trash2,
  Globe,
  Server,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { apiService } from '../services/api'
import { authService } from '../services/auth'

const ZoneEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'
  const isAdmin = authService.isAdmin()
  const queryClient = useQueryClient()
  
  const [activeTab, setActiveTab] = useState('records')
  const [previewData, setPreviewData] = useState<any>(null)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [showRecordForm, setShowRecordForm] = useState(false)
  const [recordForm, setRecordForm] = useState({
    name: '',
    type: 'A',
    value: '',
    ttl: 3600,
    priority: '',
  })
  const [zoneData, setZoneData] = useState({
    name: '',
    type: 'master',
    description: '',
    ttl: 3600,
    primary_ns: 'ns7.perfectinternet.in',
    admin_email: 'hostmaster.perfectinternet.in',
  })

  const { data: zone, isLoading } = useQuery(
    ['zone', id],
    () => isNew ? null : apiService.getZone(parseInt(id!)),
    { enabled: !isNew }
  )

  useEffect(() => {
    if (zone?.data?.zone) {
      setZoneData(zone.data.zone)
    }
  }, [zone])

  const records = zone?.data?.records || []
  const recordTypes = ['A', 'AAAA', 'CNAME', 'MX', 'NS', 'PTR', 'TXT', 'SRV', 'CAA']

  const refreshZone = () => queryClient.invalidateQueries(['zone', id])

  const saveZoneMutation = useMutation(
    () => isNew ? apiService.createZone(zoneData) : apiService.updateZone(parseInt(id!), zoneData),
    {
      onSuccess: (res: any) => {
        if (isNew && res?.zone_id) navigate(`/zones/${res.zone_id}`)
        refreshZone()
        alert('Zone saved')
      },
      onError: (error: any) => alert(error?.response?.data?.message || 'Failed to save zone'),
    }
  )

  const deployMutation = useMutation(
    () => apiService.deployZone(parseInt(id!)),
    {
      onSuccess: () => alert('Zone deployed and DNS service reloaded'),
      onError: (error: any) => alert(error?.response?.data?.message || 'Deploy failed'),
    }
  )

  const previewMutation = useMutation(
    () => apiService.get(`/zones?action=preview&id=${id}`),
    {
      onSuccess: (res: any) => {
        setPreviewData(res.data)
        setActiveTab('preview')
      },
      onError: (error: any) => alert(error?.response?.data?.message || 'Preview failed'),
    }
  )

  const saveRecordMutation = useMutation(
    () => {
      const payload: any = { ...recordForm, ttl: Number(recordForm.ttl || zoneData.ttl || 3600) }
      if (payload.priority === '') delete payload.priority
      return editingRecord
        ? apiService.updateRecord(editingRecord.id, payload)
        : apiService.post(`/zones?action=records&id=${id}`, payload)
    },
    {
      onSuccess: () => {
        setEditingRecord(null)
        setShowRecordForm(false)
        setRecordForm({ name: '', type: 'A', value: '', ttl: zoneData.ttl || 3600, priority: '' })
        refreshZone()
      },
      onError: (error: any) => alert(error?.response?.data?.message || 'Failed to save record'),
    }
  )

  const deleteRecordMutation = useMutation(
    (recordId: number) => apiService.deleteRecord(recordId),
    {
      onSuccess: refreshZone,
      onError: (error: any) => alert(error?.response?.data?.message || 'Failed to delete record'),
    }
  )

  const startRecordEdit = (record: any) => {
    setEditingRecord(record)
    setShowRecordForm(true)
    setRecordForm({
      name: record.name || '',
      type: record.type || 'A',
      value: record.value || '',
      ttl: record.ttl || zoneData.ttl || 3600,
      priority: record.priority || '',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 border-4 border-sky-600 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center space-x-4 min-w-0">
          <button
            onClick={() => navigate('/zones')}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 break-all">
              {isNew ? 'New Zone' : zoneData.name}
            </h2>
            <p className="text-slate-500">
              {isNew ? 'Create a new DNS zone' : 'Manage zone records and settings'}
            </p>
          </div>
        </div>
        
        {isAdmin && (
          <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button disabled={isNew || previewMutation.isLoading} onClick={() => previewMutation.mutate()} className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <Eye className="h-4 w-4" />
              <span>Preview</span>
            </button>
            <button disabled={isNew || deployMutation.isLoading} onClick={() => deployMutation.mutate()} className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
              <Play className="h-4 w-4" />
              <span>Deploy</span>
            </button>
            <button disabled={saveZoneMutation.isLoading} onClick={() => saveZoneMutation.mutate()} className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50">
              <Save className="h-4 w-4" />
              <span>Save</span>
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {['records', 'settings', 'soa', 'preview'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                activeTab === tab
                  ? 'border-sky-600 text-sky-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {activeTab === 'records' && (
          <div className="p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">DNS Records</h3>
              {isAdmin && (
                <button onClick={() => { setEditingRecord(null); setShowRecordForm(true); setRecordForm({ name: '', type: 'A', value: '', ttl: zoneData.ttl || 3600, priority: '' }) }} className="flex items-center justify-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors">
                  <Plus className="h-4 w-4" />
                  <span>Add Record</span>
                </button>
              )}
            </div>

            {showRecordForm && (
              <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                  <input className="px-3 py-2 border rounded-lg" placeholder="Name" value={recordForm.name} onChange={e => setRecordForm({ ...recordForm, name: e.target.value })} />
                  <select className="px-3 py-2 border rounded-lg" value={recordForm.type} onChange={e => setRecordForm({ ...recordForm, type: e.target.value })}>
                    {recordTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <input className="px-3 py-2 border rounded-lg md:col-span-2" placeholder={recordForm.type === 'PTR' ? 'hostname.example.com.' : 'Value'} value={recordForm.value} onChange={e => setRecordForm({ ...recordForm, value: e.target.value })} />
                  <input className="px-3 py-2 border rounded-lg" type="number" placeholder="TTL" value={recordForm.ttl} onChange={e => setRecordForm({ ...recordForm, ttl: e.target.value })} />
                </div>
                {recordForm.type === 'MX' && (
                  <input className="mt-3 px-3 py-2 border rounded-lg" type="number" placeholder="Priority" value={recordForm.priority} onChange={e => setRecordForm({ ...recordForm, priority: e.target.value })} />
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => { setShowRecordForm(false); setEditingRecord(null) }} className="px-4 py-2 border rounded-lg">Cancel</button>
                  <button onClick={() => saveRecordMutation.mutate()} disabled={saveRecordMutation.isLoading} className="px-4 py-2 bg-sky-600 text-white rounded-lg disabled:opacity-60">
                    {editingRecord ? 'Save Record' : 'Create Record'}
                  </button>
                </div>
              </div>
            )}
            
            <div className="border rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">TTL</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                        <Globe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                        <p>No records found for this zone.</p>
                      </td>
                    </tr>
                  ) : records.map((record: any) => (
                    <tr key={record.id} className="hover:bg-slate-50">
                      <td className="px-6 py-3 text-sm font-medium text-slate-900">{record.name}</td>
                      <td className="px-6 py-3">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-sky-100 text-sky-700">
                          {record.type}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-700 break-all">{record.value}</td>
                      <td className="px-6 py-3 text-sm text-slate-500">{record.ttl}</td>
                      <td className="px-6 py-3 text-right text-sm">
                        {isAdmin ? (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => startRecordEdit(record)} className="px-3 py-1.5 border rounded-lg text-slate-700 hover:bg-slate-100">Edit</button>
                            <button onClick={() => confirm('Delete this record?') && deleteRecordMutation.mutate(record.id)} className="px-3 py-1.5 border border-red-200 rounded-lg text-red-600 hover:bg-red-50">Delete</button>
                          </div>
                        ) : (
                          <span className="text-slate-400">{record.is_active ? 'Active' : 'Disabled'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="p-6 space-y-6 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Zone Name
              </label>
              <input
                type="text"
                value={zoneData.name}
                onChange={(e) => setZoneData({...zoneData, name: e.target.value})}
                disabled={!isNew}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 disabled:bg-slate-100"
                placeholder="example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Zone Type
              </label>
              <select
                value={zoneData.type}
                onChange={(e) => setZoneData({...zoneData, type: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
              >
                <option value="master">Master</option>
                <option value="slave">Slave</option>
                <option value="forward">Forward</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Description
              </label>
              <textarea
                value={zoneData.description}
                onChange={(e) => setZoneData({...zoneData, description: e.target.value})}
                rows={3}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                placeholder="Optional description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Default TTL (seconds)
              </label>
              <input
                type="number"
                value={zoneData.ttl}
                onChange={(e) => setZoneData({...zoneData, ttl: parseInt(e.target.value)})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>
        )}

        {activeTab === 'soa' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                NS1
              </label>
              <input
                type="text"
                value={zoneData.primary_ns}
                onChange={(e) => setZoneData({...zoneData, primary_ns: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Admin Email
              </label>
              <input
                type="text"
                value={zoneData.admin_email}
                onChange={(e) => setZoneData({...zoneData, admin_email: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
              />
            </div>
            {[
              ['serial', 'Serial'],
              ['refresh', 'Refresh'],
              ['retry', 'Retry'],
              ['expire', 'Expire'],
              ['minimum_ttl', 'Minimum TTL'],
              ['ttl', 'TTL'],
            ].map(([field, label]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
                <input
                  type="number"
                  value={zoneData[field] || ''}
                  onChange={(e) => setZoneData({ ...zoneData, [field]: Number(e.target.value) })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                />
              </div>
            ))}
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="p-6">
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-slate-200">
                <tbody className="bg-white divide-y divide-slate-200">
                  {[
                    ['Zone', zoneData.name],
                    ['Type', zoneData.type],
                    ['Storage', zoneData.file_path ? 'Managed by DNS service' : 'Not assigned'],
                    ['Serial', zoneData.serial],
                    ['TTL', zoneData.ttl],
                    ['Primary NS', zoneData.primary_ns],
                    ['Admin', zoneData.admin_email],
                    ['Refresh', zoneData.refresh],
                    ['Retry', zoneData.retry],
                    ['Expire', zoneData.expire],
                    ['Minimum TTL', zoneData.minimum_ttl],
                    ['Records', records.length],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td className="px-6 py-3 w-48 text-sm font-medium text-slate-500 bg-slate-50">{label}</td>
                      <td className="px-6 py-3 text-sm text-slate-900 break-all">{value || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ZoneEditor
